import { Fragment, useMemo, useState } from "react";
import {
  ChevronDown20Regular,
  ChevronRight20Regular,
  Delete16Regular,
} from "@fluentui/react-icons";
import { APP_CURRENCY, APP_LOCALE } from "../../../config/locale";
import type {
  WorkItemStatus,
  WorkOrderItem,
} from "../../../obras/obrasApi";
import {
  addWorkOrderDelivery,
  deleteWorkOrderDelivery,
  imputeWorkOrderCost,
  removeWorkOrderCostImputation,
} from "../../../obras/obrasApi";
import type { Supplier } from "../../../fornecedores/fornecedoresApi";
import { WorkMapExportButtons } from "./WorkMapExportButtons";
import { EntidadeExecutanteField } from "./EntidadeExecutanteField";
import { ListToolbar } from "../../../components/ListToolbar";
import {
  exportWorkOrdersToExcel,
  exportWorkOrdersToPdf,
} from "../../../obras/obrasApi";

interface WorkOrdersTabProps {
  workId: number;
  orders: WorkOrderItem[];
  vatRate: number;
  suppliers: Supplier[];
  itemStatuses: WorkItemStatus[];
  onOrderChange: (
    orderId: number,
    updates: Partial<WorkOrderItem>,
  ) => void;
  onOrderReplaced: (updatedOrder: WorkOrderItem) => void;
  onCostImputed?: () => void;
}

const formatCurrency = (value: number): string => {
  const formatted = new Intl.NumberFormat(APP_LOCALE, {
    style: "currency",
    currency: APP_CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

  return formatted.replace(/\./g, " ");
};

const formatDateInput = (value?: string | null): string => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const normalizeDateValue = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed;
};

// Alerta simples [4.2.3]: ainda por pedir e a data de necessidade já passou.
const isNeedOverdue = (
  statusCode: string,
  needDate?: string | null,
): boolean => {
  if (statusCode !== "em_aberto" || !needDate) return false;
  const parsed = new Date(needDate);
  if (Number.isNaN(parsed.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed < today;
};

const normalizeNumberInput = (value: string): string => {
  const cleaned = value.trim().replace(/\s/g, "");
  if (!cleaned) return "";

  const hasComma = cleaned.includes(",");
  const hasDot = cleaned.includes(".");

  if (hasComma && hasDot) {
    return cleaned.replace(/\./g, "").replace(",", ".");
  }

  if (hasComma) {
    return cleaned.replace(",", ".");
  }

  return cleaned;
};

const parseNumberInput = (value: string): number => {
  const normalized = normalizeNumberInput(value);
  if (!normalized) return 0;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveWastePercent = (quantity: number, wasteQuantity: number): number => {
  if (quantity <= 0) return 0;
  const effectiveWaste = wasteQuantity > 0 ? wasteQuantity : quantity;
  const percent = ((effectiveWaste / quantity) - 1) * 100;
  return percent > 0 ? percent : 0;
};

const applyWastePercent = (quantity: number, wastePercent: number): number => {
  if (quantity <= 0) return 0;
  const percent = Number.isFinite(wastePercent) ? Math.max(0, wastePercent) : 0;
  const result = quantity * (1 + percent / 100);
  return Number(result.toFixed(3));
};

const formatNumberInput = (value: number, decimals: number): string => {
  if (!Number.isFinite(value)) return "";
  return value.toLocaleString(APP_LOCALE, {
    useGrouping: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

const formatQuantity = (value: number): string => {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString(APP_LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
};

const formatDeliveryDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

interface DeliveryDraft {
  lote: string;
  plannedDate: string;
  plannedQuantity: string;
  deliveredQuantity: string;
  notes: string;
}

const emptyDeliveryDraft = (): DeliveryDraft => ({
  lote: "",
  plannedDate: "",
  plannedQuantity: "",
  deliveredQuantity: "",
  notes: "",
});

export function WorkOrdersTab({
  workId,
  orders,
  vatRate,
  suppliers,
  itemStatuses,
  onOrderChange,
  onOrderReplaced,
  onCostImputed,
}: WorkOrdersTabProps) {
  const [filter, setFilter] = useState("");
  const [numericDrafts, setNumericDrafts] = useState<
    Record<string, string>
  >({});
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(
    null,
  );
  const [deliveryDrafts, setDeliveryDrafts] = useState<
    Record<number, DeliveryDraft>
  >({});
  const [savingDeliveryOrderId, setSavingDeliveryOrderId] = useState<
    number | null
  >(null);
  const [deletingDeliveryId, setDeletingDeliveryId] = useState<
    number | null
  >(null);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [imputingOrderId, setImputingOrderId] = useState<number | null>(
    null,
  );
  const [imputeErrors, setImputeErrors] = useState<
    Record<number, string>
  >({});

  const setImputeError = (orderId: number, message: string | null) => {
    setImputeErrors((prev) => {
      const next = { ...prev };
      if (message) {
        next[orderId] = message;
      } else {
        delete next[orderId];
      }
      return next;
    });
  };

  const handleImputeCost = async (orderId: number) => {
    if (imputingOrderId !== null) return;
    setImputingOrderId(orderId);
    setImputeError(orderId, null);
    try {
      const updated = await imputeWorkOrderCost(workId, orderId);
      onOrderReplaced(updated);
      onCostImputed?.();
    } catch (error) {
      setImputeError(
        orderId,
        error instanceof Error && error.message
          ? error.message
          : "Não foi possível imputar o custo.",
      );
    } finally {
      setImputingOrderId(null);
    }
  };

  const handleRemoveImputation = async (orderId: number) => {
    if (imputingOrderId !== null) return;
    setImputingOrderId(orderId);
    setImputeError(orderId, null);
    try {
      const updated = await removeWorkOrderCostImputation(workId, orderId);
      onOrderReplaced(updated);
      onCostImputed?.();
    } catch (error) {
      setImputeError(
        orderId,
        error instanceof Error && error.message
          ? error.message
          : "Não foi possível anular a imputação.",
      );
    } finally {
      setImputingOrderId(null);
    }
  };

  const toggleExpanded = (orderId: number) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
    setDeliveryError(null);
  };

  const getDeliveryDraft = (orderId: number): DeliveryDraft =>
    deliveryDrafts[orderId] ?? emptyDeliveryDraft();

  const setDeliveryDraftField = (
    orderId: number,
    field: keyof DeliveryDraft,
    value: string,
  ) => {
    setDeliveryDrafts((prev) => ({
      ...prev,
      [orderId]: { ...getDeliveryDraft(orderId), [field]: value },
    }));
  };

  const handleAddDelivery = async (orderId: number) => {
    const draft = getDeliveryDraft(orderId);
    setSavingDeliveryOrderId(orderId);
    setDeliveryError(null);

    try {
      const updated = await addWorkOrderDelivery(workId, orderId, {
        lote: draft.lote.trim() || null,
        plannedDate: normalizeDateValue(draft.plannedDate),
        plannedQuantity: parseNumberInput(draft.plannedQuantity),
        deliveredQuantity: parseNumberInput(draft.deliveredQuantity),
        notes: draft.notes.trim() || null,
      });

      onOrderReplaced(updated);
      setDeliveryDrafts((prev) => ({
        ...prev,
        [orderId]: emptyDeliveryDraft(),
      }));
    } catch (error) {
      setDeliveryError(
        error instanceof Error && error.message
          ? error.message
          : "Não foi possível adicionar a entrega.",
      );
    } finally {
      setSavingDeliveryOrderId(null);
    }
  };

  const handleDeleteDelivery = async (
    orderId: number,
    deliveryId: number,
  ) => {
    setDeletingDeliveryId(deliveryId);
    setDeliveryError(null);

    try {
      const updated = await deleteWorkOrderDelivery(
        workId,
        orderId,
        deliveryId,
      );
      onOrderReplaced(updated);
    } catch (error) {
      setDeliveryError(
        error instanceof Error && error.message
          ? error.message
          : "Não foi possível remover a entrega.",
      );
    } finally {
      setDeletingDeliveryId(null);
    }
  };

  const supplierOptions = useMemo(
    () => {
      const values = new Set<string>();
      suppliers.forEach((supplier) => {
        const name = supplier.name?.trim();
        if (name) {
          values.add(name);
        }
      });
      return Array.from(values).sort((a, b) => a.localeCompare(b));
    },
    [suppliers],
  );

  const typeOptions = useMemo(() => {
    const values = new Set<string>();
    orders.forEach((order) => {
      if (order.type) {
        values.add(order.type);
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [orders]);

  const locationOptions = useMemo(() => {
    const values = new Set<string>();
    orders.forEach((order) => {
      if (order.location) {
        values.add(order.location);
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [orders]);

  const getDraft = (key: string): string | undefined =>
    numericDrafts[key];

  const setDraft = (key: string, value: string) => {
    setNumericDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const clearDraft = (key: string) => {
    setNumericDrafts((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleStatusChange = (orderId: number, value: string) => {
    const status = itemStatuses.find(
      (entry) => entry.id === Number(value),
    );
    if (!status) return;
    onOrderChange(orderId, {
      workItemStatusId: status.id,
      statusCode: status.code,
      statusName: status.name,
    });
  };

  const vatMultiplier = 1 + vatRate / 100;

  const shown = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return orders;
    return orders.filter((order) => {
      const haystack = [
        order.code,
        order.type,
        order.location,
        order.description,
        order.supplier,
        order.statusName,
      ]
        .filter((value): value is string => Boolean(value))
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [orders, filter]);

  const totals = useMemo(() => {
    let net = 0;
    shown.forEach((order) => {
      const wastePercent = resolveWastePercent(
        order.quantity,
        order.wasteQuantity,
      );
      const wasteQuantity = applyWastePercent(order.quantity, wastePercent);
      net += order.unitPrice * wasteQuantity;
    });
    return { net, gross: net * vatMultiplier };
  }, [shown, vatMultiplier]);

  return (
    <>
      <ListToolbar
        filter={filter}
        onFilterChange={setFilter}
        placeholder="Filtrar…"
        summary={
          <span className="text-secondary-text text-sm">
            Total s/IVA {formatCurrency(totals.net)} · c/IVA{" "}
            {formatCurrency(totals.gross)} · {shown.length}{" "}
            {shown.length === 1 ? "item" : "itens"}
          </span>
        }
      >
        <WorkMapExportButtons
          onExportExcel={() => exportWorkOrdersToExcel(workId)}
          onExportPdf={() => exportWorkOrdersToPdf(workId)}
        />
      </ListToolbar>
      <div
        className="bg-card border border-border overflow-x-auto"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full" style={{ minWidth: 2400 }}>
        <thead>
          <tr className="border-b border-border bg-muted text-left">
            <th className="px-4 py-3" style={{ width: 44 }} aria-label="Entregas" />
            <th className="px-4 py-3">Código</th>
            <th className="px-4 py-3">Tipo</th>
            <th className="px-4 py-3">Local</th>
            <th className="px-4 py-3">Descrição</th>
            <th className="px-4 py-3">Un.</th>
            <th className="px-4 py-3">Qtd.</th>
            <th className="px-4 py-3 text-right">Saldo</th>
            <th className="px-4 py-3">Estado</th>
            <th className="px-4 py-3">Necessidade</th>
            <th className="px-4 py-3">Entrega</th>
            <th className="px-4 py-3">Resp. receção</th>
            <th className="px-4 py-3">Notas</th>
            <th className="px-4 py-3">Preço s/IVA</th>
            <th className="px-4 py-3">Preço c/IVA</th>
            <th className="px-4 py-3">Fornecedor</th>
            <th className="px-4 py-3">Desperdício (%)</th>
            <th className="px-4 py-3">Total s/IVA</th>
            <th className="px-4 py-3">Total c/IVA</th>
            <th className="px-4 py-3">Imputação</th>
          </tr>
        </thead>
        <tbody>
          {filter !== "" && shown.length === 0 && (
            <tr>
              <td
                className="px-4 py-3 text-secondary-text text-sm"
                colSpan={20}
              >
                Sem resultados para o filtro.
              </td>
            </tr>
          )}
          {shown.map((order) => {
            const quantity = order.quantity;
            const wastePercent = resolveWastePercent(quantity, order.wasteQuantity);
            const wasteQuantity = applyWastePercent(quantity, wastePercent);
            const unitNet = order.unitPrice;
            const unitGross = unitNet * vatMultiplier;
            const totalNet = unitNet * wasteQuantity;
            const totalGross = totalNet * vatMultiplier;
            const overdue = isNeedOverdue(order.statusCode, order.needDate);

            const quantityKey = `${order.id}-quantity`;
            const wasteKey = `${order.id}-wastePercent`;
            const unitPriceKey = `${order.id}-unitPrice`;

            const quantityValue =
              getDraft(quantityKey) ??
              formatNumberInput(quantity, 3);
            const wasteValue =
              getDraft(wasteKey) ??
              formatNumberInput(Number(wastePercent.toFixed(1)), 2);
            const unitPriceValue =
              getDraft(unitPriceKey) ??
              formatNumberInput(unitNet, 2);

            const isExpanded = expandedOrderId === order.id;
            const deliveries = order.deliveries ?? [];
            const balance = order.balance;
            const deliveryDraft = getDeliveryDraft(order.id);
            const draftHasQty =
              deliveryDraft.plannedQuantity.trim() !== "" ||
              deliveryDraft.deliveredQuantity.trim() !== "";
            const draftDiff =
              (parseFloat(deliveryDraft.plannedQuantity.replace(",", ".")) ||
                0) -
              (parseFloat(deliveryDraft.deliveredQuantity.replace(",", ".")) ||
                0);

            return (
              <Fragment key={order.id}>
              <tr
                className="border-b border-border last:border-b-0"
              >
                <td className="px-4 py-3 align-top">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(order.id)}
                    className="p-1 text-secondary-text hover:text-foreground"
                    aria-expanded={isExpanded}
                    aria-label={
                      isExpanded ? "Ocultar entregas" : "Mostrar entregas"
                    }
                  >
                    {isExpanded ? (
                      <ChevronDown20Regular className="w-5 h-5" />
                    ) : (
                      <ChevronRight20Regular className="w-5 h-5" />
                    )}
                  </button>
                </td>
                <td className="px-4 py-3 align-top">
                  <input
                    type="text"
                    value={order.code}
                    onChange={(event) =>
                      onOrderChange(order.id, {
                        code: event.target.value,
                      })
                    }
                    className="w-24 px-2 py-1 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top">
                  <input
                    type="text"
                    list="order-type-options"
                    value={order.type ?? ""}
                    onChange={(event) =>
                      onOrderChange(order.id, {
                        type: event.target.value,
                      })
                    }
                    className="w-32 px-2 py-1 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top">
                  <input
                    type="text"
                    list="order-location-options"
                    value={order.location ?? ""}
                    onChange={(event) =>
                      onOrderChange(order.id, {
                        location: event.target.value,
                      })
                    }
                    className="w-48 px-2 py-1 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top" style={{ minWidth: 340 }}>
                  <textarea
                    value={order.description}
                    rows={2}
                    onChange={(event) =>
                      onOrderChange(order.id, {
                        description: event.target.value,
                      })
                    }
                    className="w-full px-2 py-1 border border-border rounded bg-background text-foreground resize-y"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top">
                  <input
                    type="text"
                    value={order.unit}
                    onChange={(event) =>
                      onOrderChange(order.id, {
                        unit: event.target.value,
                      })
                    }
                    className="w-16 px-2 py-1 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top">
                  <input
                    type="text"
                    value={quantityValue}
                    onChange={(event) =>
                      setDraft(quantityKey, event.target.value)
                    }
                    onBlur={(event) => {
                      const parsed = parseNumberInput(event.target.value);
                      onOrderChange(order.id, { quantity: parsed });
                      clearDraft(quantityKey);
                    }}
                    className="w-24 px-2 py-1 border border-border rounded bg-background text-foreground text-right"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td
                  className={`px-4 py-3 align-top text-right ${
                    balance < 0 ? "text-red-600" : ""
                  }`}
                >
                  {formatQuantity(balance)}
                </td>
                <td className="px-4 py-3 align-top">
                  <select
                    value={order.workItemStatusId || ""}
                    onChange={(event) =>
                      handleStatusChange(order.id, event.target.value)
                    }
                    className="w-48 px-2 py-1 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {itemStatuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 align-top">
                  <input
                    type="date"
                    value={formatDateInput(order.needDate)}
                    onChange={(event) =>
                      onOrderChange(order.id, {
                        needDate: normalizeDateValue(event.target.value),
                      })
                    }
                    className="w-48 px-2 py-1 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                  {overdue && (
                    <div className="text-destructive text-xs mt-1">
                      Devia ter sido pedido
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 align-top">
                  <input
                    type="date"
                    value={formatDateInput(order.deliveryDate)}
                    onChange={(event) =>
                      onOrderChange(order.id, {
                        deliveryDate: normalizeDateValue(event.target.value),
                      })
                    }
                    className="w-48 px-2 py-1 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top">
                  <input
                    type="text"
                    value={order.receptionResponsible ?? ""}
                    onChange={(event) =>
                      onOrderChange(order.id, {
                        receptionResponsible: event.target.value,
                      })
                    }
                    className="w-48 px-2 py-1 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top" style={{ minWidth: 240 }}>
                  <textarea
                    value={order.deliveryNotes ?? ""}
                    rows={2}
                    onChange={(event) =>
                      onOrderChange(order.id, {
                        deliveryNotes: event.target.value,
                      })
                    }
                    className="w-full px-2 py-1 border border-border rounded bg-background text-foreground resize-y"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top text-right">
                  <input
                    type="text"
                    value={unitPriceValue}
                    onChange={(event) =>
                      setDraft(unitPriceKey, event.target.value)
                    }
                    onBlur={(event) => {
                      const parsed = parseNumberInput(event.target.value);
                      onOrderChange(order.id, { unitPrice: parsed });
                      clearDraft(unitPriceKey);
                    }}
                    className="w-32 px-2 py-1 border border-border rounded bg-background text-foreground text-right"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top text-right text-secondary-text">
                  {formatCurrency(unitGross)}
                </td>
                <td className="px-4 py-3 align-top">
                  <EntidadeExecutanteField
                    value={order.supplier ?? ""}
                    onChange={(valor) =>
                      onOrderChange(order.id, { supplier: valor })
                    }
                    options={supplierOptions}
                    inputClassName="w-48 px-2 py-1 border border-border rounded bg-background text-foreground"
                    placeholder="Fornecedor"
                    panelPosition="fixed"
                    ariaLabel="Fornecedor"
                  />
                </td>
                <td className="px-4 py-3 align-top">
                  <input
                    type="text"
                    value={wasteValue}
                    onChange={(event) =>
                      setDraft(wasteKey, event.target.value)
                    }
                    onBlur={(event) => {
                      const parsed = parseNumberInput(event.target.value);
                      const updatedWaste = applyWastePercent(quantity, parsed);
                      onOrderChange(order.id, { wasteQuantity: updatedWaste });
                      clearDraft(wasteKey);
                    }}
                    className="w-24 px-2 py-1 border border-border rounded bg-background text-foreground text-right"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top text-right">
                  {formatCurrency(totalNet)}
                </td>
                <td className="px-4 py-3 align-top text-right text-secondary-text">
                  {formatCurrency(totalGross)}
                </td>
                <td className="px-4 py-3 align-top" style={{ minWidth: 180 }}>
                  {order.imputedCostId != null ? (
                    <div className="flex flex-col gap-1" style={{ alignItems: "flex-start" }}>
                      <span
                        className="inline-flex items-center px-2 py-0.5 text-xs bg-primary/10 text-primary border border-primary/20"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        Imputado · {formatCurrency(order.imputedAmount ?? 0)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveImputation(order.id)}
                        disabled={imputingOrderId === order.id}
                        className="text-destructive underline text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {imputingOrderId === order.id ? "A anular…" : "Anular"}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleImputeCost(order.id)}
                      disabled={imputingOrderId === order.id}
                      className="text-primary underline text-xs disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {imputingOrderId === order.id
                        ? "A imputar…"
                        : "Imputar custo"}
                    </button>
                  )}
                  {imputeErrors[order.id] && (
                    <div className="text-destructive text-xs mt-1">
                      {imputeErrors[order.id]}
                    </div>
                  )}
                </td>
              </tr>
              {isExpanded && (
                <tr className="border-b border-border last:border-b-0">
                  <td className="px-4 py-3 bg-muted" colSpan={20}>
                    <div
                      style={{
                        position: "sticky",
                        left: 0,
                        width: 1080,
                        maxWidth: "100%",
                      }}
                    >
                    {deliveryError && (
                      <div
                        className="border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 mb-3 text-sm"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        {deliveryError}
                      </div>
                    )}
                    <div
                      className="bg-card border border-border overflow-x-auto"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      <table className="w-full" style={{ minWidth: 880 }}>
                        <thead>
                          <tr className="border-b border-border text-left">
                            <th
                              className="px-3 py-2"
                              style={{ width: 170 }}
                            >
                              Lote
                            </th>
                            <th
                              className="px-3 py-2"
                              style={{ width: 170 }}
                            >
                              Data prevista
                            </th>
                            <th
                              className="px-3 py-2 text-right"
                              style={{ width: 130 }}
                            >
                              Qtd. prevista
                            </th>
                            <th
                              className="px-3 py-2 text-right"
                              style={{ width: 130 }}
                            >
                              Qtd. entregue
                            </th>
                            <th
                              className="px-3 py-2 text-right"
                              style={{ width: 110 }}
                            >
                              Diferença
                            </th>
                            <th className="px-3 py-2">Notas</th>
                            <th
                              className="px-3 py-2"
                              style={{ width: 56 }}
                              aria-label="Ações"
                            />
                          </tr>
                        </thead>
                        <tbody>
                          {deliveries.length === 0 ? (
                            <tr>
                              <td
                                className="px-3 py-3 text-secondary-text text-sm"
                                colSpan={7}
                              >
                                Sem entregas registadas.
                              </td>
                            </tr>
                          ) : (
                            deliveries.map((delivery) => {
                              const difference =
                                delivery.plannedQuantity -
                                delivery.deliveredQuantity;
                              return (
                                <tr
                                  key={delivery.id}
                                  className="border-b border-border last:border-b-0"
                                >
                                  <td className="px-3 py-2 align-top">
                                    {delivery.lote || "—"}
                                  </td>
                                  <td className="px-3 py-2 align-top">
                                    {formatDeliveryDate(
                                      delivery.plannedDate,
                                    )}
                                  </td>
                                  <td className="px-3 py-2 align-top text-right">
                                    {formatQuantity(
                                      delivery.plannedQuantity,
                                    )}
                                  </td>
                                  <td className="px-3 py-2 align-top text-right">
                                    {formatQuantity(
                                      delivery.deliveredQuantity,
                                    )}
                                  </td>
                                  <td
                                    className={`px-3 py-2 align-top text-right ${
                                      difference < 0 ? "text-red-600" : ""
                                    }`}
                                  >
                                    {formatQuantity(difference)}
                                  </td>
                                  <td className="px-3 py-2 align-top">
                                    {delivery.notes || "—"}
                                  </td>
                                  <td className="px-3 py-2 align-top text-right">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleDeleteDelivery(
                                          order.id,
                                          delivery.id,
                                        )
                                      }
                                      disabled={
                                        deletingDeliveryId === delivery.id
                                      }
                                      className="p-1 text-secondary-text hover:text-destructive disabled:opacity-50 disabled:cursor-not-allowed"
                                      aria-label="Remover entrega"
                                    >
                                      <Delete16Regular className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-border bg-muted/40">
                            <td className="px-3 py-2 align-top">
                              <input
                                type="text"
                                value={deliveryDraft.lote}
                                onChange={(event) =>
                                  setDeliveryDraftField(
                                    order.id,
                                    "lote",
                                    event.target.value,
                                  )
                                }
                                placeholder="Lote"
                                className="w-full px-2 py-1 border border-border rounded bg-background text-foreground"
                                style={{ borderRadius: "var(--radius)" }}
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <input
                                type="date"
                                value={formatDateInput(
                                  deliveryDraft.plannedDate || null,
                                )}
                                onChange={(event) =>
                                  setDeliveryDraftField(
                                    order.id,
                                    "plannedDate",
                                    event.target.value,
                                  )
                                }
                                className="w-full px-2 py-1 border border-border rounded bg-background text-foreground"
                                style={{ borderRadius: "var(--radius)" }}
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <input
                                type="text"
                                inputMode="decimal"
                                value={deliveryDraft.plannedQuantity}
                                onChange={(event) =>
                                  setDeliveryDraftField(
                                    order.id,
                                    "plannedQuantity",
                                    event.target.value,
                                  )
                                }
                                placeholder="0"
                                className="w-full px-2 py-1 border border-border rounded bg-background text-foreground text-right"
                                style={{ borderRadius: "var(--radius)" }}
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <input
                                type="text"
                                inputMode="decimal"
                                value={deliveryDraft.deliveredQuantity}
                                onChange={(event) =>
                                  setDeliveryDraftField(
                                    order.id,
                                    "deliveredQuantity",
                                    event.target.value,
                                  )
                                }
                                placeholder="0"
                                className="w-full px-2 py-1 border border-border rounded bg-background text-foreground text-right"
                                style={{ borderRadius: "var(--radius)" }}
                              />
                            </td>
                            <td
                              className={`px-3 py-2 text-right ${
                                draftHasQty && draftDiff < 0
                                  ? "text-red-600"
                                  : "text-secondary-text"
                              }`}
                            >
                              {draftHasQty ? formatQuantity(draftDiff) : "—"}
                            </td>
                            <td className="px-3 py-2 align-top">
                              <input
                                type="text"
                                value={deliveryDraft.notes}
                                onChange={(event) =>
                                  setDeliveryDraftField(
                                    order.id,
                                    "notes",
                                    event.target.value,
                                  )
                                }
                                placeholder="Notas"
                                className="w-full px-2 py-1 border border-border rounded bg-background text-foreground"
                                style={{ borderRadius: "var(--radius)" }}
                              />
                            </td>
                            <td className="px-3 py-2" />
                          </tr>
                          <tr className="bg-muted/40">
                            <td
                              className="px-3"
                              colSpan={7}
                              style={{ paddingTop: 4, paddingBottom: 12 }}
                            >
                              <button
                                type="button"
                                onClick={() => handleAddDelivery(order.id)}
                                disabled={savingDeliveryOrderId === order.id}
                                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ borderRadius: "var(--radius-button)" }}
                              >
                                {savingDeliveryOrderId === order.id
                                  ? "A adicionar..."
                                  : "Adicionar entrega"}
                              </button>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      <datalist id="order-type-options">
        {typeOptions.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
      <datalist id="order-location-options">
        {locationOptions.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
      </div>
    </>
  );
}
